import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, finalize } from 'rxjs/operators';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { VehiclesService } from '../../../services/vehicles.service';
import { PaymentService } from '../../../services/payment.service';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { AccountsReceivableCreate } from '../../../models/AccountsReceivable';
import { AsaasCharge, AsaasChargeBatch, AsaasCustomer } from '../../../models/Payment';
import { B2B_ADDITIONAL_CHARGE_TYPE_OPTIONS } from '../../../constants/b2b-additional-charge-types';

@Component({
  selector: 'app-b2b-contracts-item-form',
  templateUrl: './b2b-contracts-item-form.component.html',
  styleUrls: ['./b2b-contracts-item-form.component.css']
})
export class B2bContractsItemFormComponent implements OnInit, OnDestroy {
  itemForm: FormGroup;
  isSubmitting = false;
  isLoading = false;
  contractId: number | null = null;
  /** Definido na rota `.../items/:itemId/edit` — modo edição de pedido existente. */
  itemId: number | null = null;
  /** Número do pedido vindo da API (somente edição). */
  loadedItemNumber = '';
  contractNumber = '';
  contractName = '';
  /** Contrato carregado (para criar cliente Asaas se ainda não existir — mesmo padrão do fluxo de contrato). */
  private contractB2b: any = null;
  currentStep = 1;
  readonly totalSteps = 2;
  asaasCustomerId: string | null = null;
  asaasCustomerLoading = false;
  asaasCustomerError: string | null = null;
  /** Dados do cliente Asaas (cartão / titular) — alinhado ao fluxo de contrato. */
  asaasCustomerInfo: AsaasCustomer | null = null;

  paymentMethods = [
    { value: 'CASH', label: 'Dinheiro' },
    { value: 'PIX', label: 'Pix' },
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'CREDIT_CARD', label: 'Cartão' }
  ];

  depositPayments = [
    { method: 'BOLETO', amount: 0 },
    { method: 'PIX', amount: 0 }
  ];
  firstInstallmentPayments = [
    { method: 'BOLETO', amount: 0 },
    { method: 'PIX', amount: 0 }
  ];
  depositPaymentMode: 'single' | 'split' = 'single';
  firstInstallmentPaymentMode: 'single' | 'split' = 'single';
  /** Parcelamento do boleto da caução (1x a 10x). */
  depositInstallments = 1;
  depositCardInstallments = 1;
  depositCardInstallments2 = 1;

  creditCardNumber = '';
  creditCardHolderName = '';
  creditCardExpiryMonth = '';
  creditCardExpiryYear = '';
  creditCardCcv = '';
  creditCardNumber2 = '';
  creditCardHolderName2 = '';
  creditCardExpiryMonth2 = '';
  creditCardExpiryYear2 = '';
  creditCardCcv2 = '';
  contractTypeOptions = ['Locação', 'Venda', 'Comodato'];
  deliveryTypeOptions = ['Retirada', 'Entrega'];
  statusOptions = [
    { value: 'Active', label: 'Ativo' },
    { value: 'Inactive', label: 'Inativo' },
    { value: 'Pending', label: 'Pendente' }
  ];
  /** UFs para controle de localização do pedido (sempre editável). */
  readonly brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];
  currentYear = new Date().getFullYear();
  maxYear = this.currentYear + 1;
  isSearchingVehicle = false;
  private deliveryTypeSub?: Subscription;
  private installmentPreviewSub?: Subscription;
  /** Chave: `{placa}-{número da parcela}` */
  installmentDueDateOverrides: Record<string, string> = {};
  adjustWeekendToPreviousBusinessDay = false;

  installmentPreviewRows: Array<{
    plate: string;
    installmentNumber: number;
    dueDate: string;
    amount: number;
    isWeekend: boolean;
    overrideKey: string;
  }> = [];
  extraCharges: Array<{
    description: string;
    amount: number | null;
    dueDate: string;
  }> = [];
  readonly additionalChargeTypeOptions = B2B_ADDITIONAL_CHARGE_TYPE_OPTIONS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private vehiclesService: VehiclesService,
    private paymentService: PaymentService,
    private accountsReceivableService: AccountsReceivableService,
    private fb: FormBuilder
  ) {
    this.itemForm = this.createItemForm();
  }

  ngOnInit(): void {
    this.applyDeliveryAddressValidators(this.itemForm.get('deliveryType')?.value);
    this.deliveryTypeSub = this.itemForm.get('deliveryType')?.valueChanges.subscribe((v) => {
      this.applyDeliveryAddressValidators(v);
    });
    this.setupInstallmentPreviewRefresh();

    this.route.params.subscribe(params => {
      this.contractId = params['id'] ? +params['id'] : null;
      const rawPedido = params['itemId'];
      this.itemId =
        rawPedido != null && rawPedido !== '' && !isNaN(+rawPedido) ? +rawPedido : null;
      if (this.contractId) {
        this.loadContractInfo();
        if (this.isEditMode) {
          this.loadItemForEdit();
        } else {
          this.initFormWithDefaults();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.deliveryTypeSub?.unsubscribe();
    this.installmentPreviewSub?.unsubscribe();
  }

  /** Endereço (estado, cidade, polo) só é obrigatório quando a entrega é no local do cliente. */
  get isEntregaDelivery(): boolean {
    return this.itemForm.get('deliveryType')?.value === 'Entrega';
  }

  get isEditMode(): boolean {
    return this.itemId != null && !isNaN(this.itemId) && this.itemId > 0;
  }

  private applyDeliveryAddressValidators(deliveryType: string | null | undefined): void {
    const state = this.itemForm.get('state');
    const city = this.itemForm.get('city');
    const pole = this.itemForm.get('pole');
    if (!state || !city || !pole) return;

    // Localização permanece editável em qualquer tipo de entrega (controle da equipe).
    // Só fica obrigatória quando a entrega é no local do cliente.
    if (deliveryType === 'Entrega') {
      state.setValidators([Validators.required]);
      city.setValidators([Validators.required]);
      pole.setValidators([Validators.required]);
    } else {
      state.clearValidators();
      city.clearValidators();
      pole.clearValidators();
    }
    state.updateValueAndValidity({ emitEvent: false });
    city.updateValueAndValidity({ emitEvent: false });
    pole.updateValueAndValidity({ emitEvent: false });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contractB2b = contract;
        this.contractNumber = contract.contractNumber || '';
        this.contractName = contract.contractName || '';
        this.loadAsaasCustomerByCpfCnpj((contract?.cnpj || '').toString());
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  initFormWithDefaults(): void {
    this.loadedItemNumber = '';
    while (this.vehicleSnapshots.length !== 0) {
      this.vehicleSnapshots.removeAt(0);
    }
    this.itemForm.patchValue({
      vehicleQuantity: 1,
      status: 'Active',
      paymentType: 'BOLETO',
      contractType: 'Locação',
      deliveryType: 'Retirada',
      installmentCount: 12,
      depositAmount: 0,
      firstInstallmentAmount: 0
    });
    this.initOrderPaymentSplits();
    this.resetExtraCharges();
    this.vehicleSnapshots.push(this.createVehicleFormGroup());
    this.refreshInstallmentPreview();
  }

  /** Converte data ISO ou Date em `yyyy-MM-dd` para input type="date". */
  private isoToDateInput(value: string | Date | null | undefined): string {
    if (value == null || value === '') return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private normalizeOptionalText(value: unknown): string | null {
    const text = String(value ?? '').trim();
    return text ? text : null;
  }

  loadItemForEdit(): void {
    if (!this.contractId || !this.itemId) return;
    this.isLoading = true;
    this.contractsService.getB2bContractItemById(this.contractId, this.itemId).subscribe({
      next: (item) => {
        this.loadedItemNumber = (item?.itemNumber || '').toString();
        while (this.vehicleSnapshots.length !== 0) {
          this.vehicleSnapshots.removeAt(0);
        }

        const snaps: any[] = Array.isArray(item?.vehicleSnapshots) ? item.vehicleSnapshots : [];
        const qty = Math.max(1, Number(item?.quantity) || snaps.length || 1);

        this.itemForm.patchValue({
          motorcycleType: item?.motorcycleType ?? '',
          unitValue: item?.unitValue ?? 0,
          paymentType: item?.paymentType ?? 'BOLETO',
          installmentCount: item?.installmentCount ?? 12,
          depositAmount: 0,
          firstInstallmentAmount: 0,
          state: String(item?.state ?? '').trim().toUpperCase(),
          city: item?.city ?? '',
          contractType: item?.contractType ?? 'Locação',
          deliveryType: item?.deliveryType ?? 'Retirada',
          pole: item?.pole ?? '',
          deliveryDate: this.isoToDateInput(item?.deliveryDate),
          returnDate: item?.returnDate ? this.isoToDateInput(item.returnDate) : null,
          customerPickupDate: item?.customerPickupDate ? this.isoToDateInput(item.customerPickupDate) : null,
          accessoriesDescription: item?.accessoriesDescription ?? '',
          status: item?.status ?? 'Active',
          vehicleQuantity: qty
        });
        this.applyDeliveryAddressValidators(this.itemForm.get('deliveryType')?.value);

        if (snaps.length === 0) {
          for (let i = 0; i < qty; i++) {
            this.vehicleSnapshots.push(this.createVehicleFormGroup());
          }
        } else {
          snaps.forEach((vs) => {
            const g = this.createVehicleFormGroup();
            g.patchValue({
              snapshotId: vs.id ?? vs.Id ?? null,
              vehicleId: vs.vehicleId ?? vs.VehicleId ?? null,
              plate: vs.plate ?? vs.Plate ?? '',
              model: vs.model ?? vs.Model ?? '',
              year: vs.year ?? vs.Year ?? new Date().getFullYear(),
              color: vs.color ?? vs.Color ?? '',
              brand: vs.brand ?? vs.Brand ?? '',
              renavam: vs.renavam ?? vs.Renavam ?? '',
              chassis: vs.chassis ?? vs.Chassis ?? '',
              currentKm: vs.currentKm ?? vs.CurrentKm ?? 0,
              inspectionNotes: vs.inspectionNotes ?? vs.InspectionNotes ?? '',
              trackerType: vs.trackerType ?? vs.TrackerType ?? '',
              trackerId: vs.trackerId ?? vs.TrackerId ?? '',
              hasBox: vs.hasBox ?? vs.HasBox ?? false,
              operationalStatus: vs.operationalStatus ?? vs.OperationalStatus ?? 'ATIVO'
            });
            this.vehicleSnapshots.push(g);
          });
          while (this.vehicleSnapshots.length < qty) {
            this.vehicleSnapshots.push(this.createVehicleFormGroup());
          }
          while (this.vehicleSnapshots.length > qty) {
            this.vehicleSnapshots.removeAt(this.vehicleSnapshots.length - 1);
          }
        }

        this.initOrderPaymentSplits();
        this.resetExtraCharges();
        this.refreshInstallmentPreview();
        this.isLoading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
        this.navigateBack();
      }
    });
  }

  /**
   * Corpo do PUT `pedidos/editar` — alinhado ao exemplo da API e ao payload de criação em lote.
   */
  private buildEditPedidoPayload(formValue: any): any {
    const vehicleSnapshots = this.vehicleSnapshots.controls.map((control) => {
      const v = control.value;
      const snap: Record<string, unknown> = {
        vehicleId: v.vehicleId ?? null,
        plate: v.plate,
        model: v.model,
        year: v.year,
        color: v.color,
        brand: v.brand,
        renavam: v.renavam,
        chassis: v.chassis,
        currentKm: v.currentKm,
        inspectionNotes: v.inspectionNotes || null,
        trackerType: v.trackerType || null,
        trackerId: v.trackerId || null,
        hasBox: v.hasBox || false,
        operationalStatus: v.operationalStatus || null
      };
      const sid = v.snapshotId;
      if (sid != null && sid !== '') {
        snap['id'] = typeof sid === 'number' ? sid : +sid;
      }
      return snap;
    });

    return {
      itemNumber: this.loadedItemNumber || '',
      itemNumberPrefix: 'Pedido',
      motorcycleType: formValue.motorcycleType,
      quantity: Number(formValue.vehicleQuantity) || vehicleSnapshots.length,
      unitValue: formValue.unitValue,
      paymentType: formValue.paymentType,
      installmentCount: formValue.installmentCount,
      depositAmount: Number(formValue.depositAmount) || 0,
      usesMonthlyClosing: true,
      contractType: formValue.contractType,
      deliveryType: formValue.deliveryType,
      deliveryDate: formValue.deliveryDate ? new Date(formValue.deliveryDate).toISOString() : null,
      status: formValue.status,
      state: this.normalizeOptionalText(formValue.state),
      city: this.normalizeOptionalText(formValue.city),
      pole: this.normalizeOptionalText(formValue.pole),
      returnDate: formValue.returnDate ? new Date(formValue.returnDate).toISOString() : null,
      customerPickupDate: formValue.customerPickupDate ? new Date(formValue.customerPickupDate).toISOString() : null,
      accessoriesDescription: formValue.accessoriesDescription || null,
      vehicleSnapshots
    };
  }

  createItemForm(): FormGroup {
    return this.fb.group({
      motorcycleType: ['', [Validators.required]],
      unitValue: [0, [Validators.required, Validators.min(0)]],
      paymentType: ['BOLETO', [Validators.required]],
      installmentCount: [12, [Validators.required, Validators.min(1), Validators.max(24)]],
      depositAmount: [0, [Validators.min(0)]],
      firstInstallmentAmount: [0, [Validators.min(0)]],
      state: [''],
      city: [''],
      contractType: ['Locação', [Validators.required]],
      deliveryType: ['Retirada', [Validators.required]],
      pole: [''],
      deliveryDate: ['', [Validators.required]],
      returnDate: [null],
      customerPickupDate: [null],
      accessoriesDescription: [''],
      status: ['Active', [Validators.required]],
      vehicleQuantity: [1, [Validators.required, Validators.min(1)]],
      vehicleSnapshots: this.fb.array([])
    });
  }

  get vehicleSnapshots(): FormArray {
    return this.itemForm.get('vehicleSnapshots') as FormArray;
  }

  createVehicleFormGroup(): FormGroup {
    return this.fb.group({
      /** ID do snapshot B2B (edição) — enviado no PUT para o backend alinhar veículos. */
      snapshotId: [null as number | null],
      vehicleId: [null as number | null],
      plate: ['', [Validators.required]],
      model: ['', [Validators.required]],
      year: [new Date().getFullYear(), [Validators.required, Validators.min(1900)]],
      color: ['', [Validators.required]],
      brand: ['', [Validators.required]],
      renavam: ['', [Validators.required]],
      chassis: ['', [Validators.required]],
      currentKm: [0, [Validators.required, Validators.min(0)]],
      inspectionNotes: [''],
      trackerType: [''],
      trackerId: [''],
      hasBox: [false],
      operationalStatus: ['ATIVO']
    });
  }

  onVehicleQuantityChange(): void {
    const quantity = this.itemForm.get('vehicleQuantity')?.value || 0;
    const currentCount = this.vehicleSnapshots.length;
    if (quantity > currentCount) {
      for (let i = currentCount; i < quantity; i++) {
        this.vehicleSnapshots.push(this.createVehicleFormGroup());
      }
    } else if (quantity < currentCount) {
      for (let i = currentCount; i > quantity; i--) {
        this.vehicleSnapshots.removeAt(i - 1);
      }
    }
    this.refreshInstallmentPreview();
  }

  /**
   * Busca dados do veículo pela placa e preenche o formulário do veículo no índice informado.
   */
  searchVehicleByPlate(index: number): void {
    const vehicleGroup = this.vehicleSnapshots.at(index);
    if (!vehicleGroup) return;

    const rawPlate = (vehicleGroup.get('plate')?.value || '').toString().trim();
    if (!rawPlate) {
      this.errorService.showErrorMessage('Informe a placa para buscar o veículo.');
      return;
    }

    const plate = rawPlate.replace(/\s+/g, '').toUpperCase();
    this.isSearchingVehicle = true;

    this.vehiclesService.getByPlate(plate).subscribe({
      next: (vehicle) => {
        this.isSearchingVehicle = false;

        if (!vehicle) {
          this.errorService.showErrorMessage(`Nenhum veículo encontrado com a placa ${plate}.`);
          return;
        }

        vehicleGroup.patchValue({
          vehicleId: vehicle.id,
          plate: vehicle.plateNumber || plate,
          model: vehicle.model || '',
          year: vehicle.modelYear || vehicle.manufactureYear || new Date().getFullYear(),
          color: vehicle.color || '',
          brand: vehicle.brand || '',
          renavam: vehicle.renavam || vehicle.renavamNumber || '',
          chassis: vehicle.chassisNumber || '',
          currentKm: vehicle.currentMileage ?? 0,
          trackerId: vehicle.trackerId || '',
          hasBox: vehicle.hasBox ?? false
        });

        this.errorService.showSuccessMessage('Dados do veículo carregados pela placa.');
      },
      error: (err) => {
        this.isSearchingVehicle = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  /**
   * Gera o próximo `itemNumber` (Pedido) evitando repetir "001".
   * Observação: o backend deveria garantir unicidade, mas aqui prevenimos colisões
   * quando a API retorna sempre o mesmo número.
   */
  private resolveNextPedidoItemNumber(contractId: number): Observable<number> {
    return this.contractsService.getB2bContractItems(contractId, 1, 5000).pipe(
      map((res) => {
        const items: any[] = res?.data || [];
        let max = 0;
        for (const it of items) {
          const raw = (it?.itemNumber ?? it?.ItemNumber ?? '').toString().trim();
          if (!raw) continue;
          const n = parseInt(raw.replace(/\D/g, ''), 10);
          if (!isNaN(n) && n > max) max = n;
        }
        return Math.max(1, max + 1);
      }),
      catchError(() => {
        // Fallback: ainda assim único (timestamp reduzido), caso a listagem falhe
        const t = Date.now().toString();
        const n = parseInt(t.slice(-6), 10);
        return of(isNaN(n) ? 1 : Math.max(1, n));
      })
    );
  }

  onSubmit(): void {
    if (this.currentStep !== 2) {
      this.goToStep(2);
      return;
    }

    if (this.itemForm.invalid || !this.contractId) {
      this.itemForm.markAllAsTouched();
      this.vehicleSnapshots.controls.forEach(c => c.markAllAsTouched());
      return;
    }
    if (!this.validateOrderPayments()) return;
    if (!this.validateDepositCardIfNeeded()) return;
    const vehicleQuantity = this.itemForm.get('vehicleQuantity')?.value || 0;
    if (this.vehicleSnapshots.length !== vehicleQuantity) {
      this.errorService.showErrorMessage('Por favor, preencha todos os veículos.');
      return;
    }

    this.isSubmitting = true;
    const formValue = this.itemForm.value;
    const vehicleSnapshots = this.vehicleSnapshots.controls.map(control => {
      const v = control.value;
      return {
        vehicleId: v.vehicleId ?? null,
        plate: v.plate,
        model: v.model,
        year: v.year,
        color: v.color,
        brand: v.brand,
        renavam: v.renavam,
        chassis: v.chassis,
        currentKm: v.currentKm,
        inspectionNotes: v.inspectionNotes || null,
        trackerType: v.trackerType || null,
        trackerId: v.trackerId || null,
        hasBox: v.hasBox || false,
        operationalStatus: v.operationalStatus || null
      };
    });

    const itemData = {
      itemNumberPrefix: 'Pedido',
      motorcycleType: formValue.motorcycleType,
      unitValue: formValue.unitValue,
      paymentType: formValue.paymentType,
      installmentCount: formValue.installmentCount,
      state: this.normalizeOptionalText(formValue.state),
      city: this.normalizeOptionalText(formValue.city),
      contractType: formValue.contractType,
      deliveryType: formValue.deliveryType,
      pole: this.normalizeOptionalText(formValue.pole),
      deliveryDate: formValue.deliveryDate ? new Date(formValue.deliveryDate).toISOString() : null,
      returnDate: formValue.returnDate ? new Date(formValue.returnDate).toISOString() : null,
      customerPickupDate: formValue.customerPickupDate ? new Date(formValue.customerPickupDate).toISOString() : null,
      accessoriesDescription: formValue.accessoriesDescription || null,
      status: formValue.status,
      vehicleSnapshots
    };

    if (this.isEditMode && this.contractId && this.itemId) {
      const editPayload = this.buildEditPedidoPayload(formValue);
      this.contractsService.editB2bPedido(this.contractId, this.itemId, editPayload).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.errorService.showSuccessMessage('Pedido atualizado com sucesso!');
          this.navigateBack();
        },
        error: (err) => {
          this.errorService.handleHttpError(err);
          this.isSubmitting = false;
        }
      });
      return;
    }

    this.resolveNextPedidoItemNumber(this.contractId)
      .pipe(
        switchMap((nextItemNumber) => {
          const payload = {
            ...itemData,
            // Enviar como número para facilitar parse do backend
            itemNumber: nextItemNumber,
            // Algumas APIs aceitam o campo em PascalCase também
            ItemNumber: nextItemNumber
          };
          // eslint-disable-next-line no-console
          console.log('[B2bContractsItemForm] Creating order with itemNumber:', nextItemNumber, 'payload:', payload);
          return this.contractsService.addB2bContractItemBatch(this.contractId as number, payload).pipe(
            map((createdItemResponse) => {
              // eslint-disable-next-line no-console
              console.log('[B2bContractsItemForm] Created response:', createdItemResponse);
              return { createdItemResponse, payload };
            })
          );
        }),
        switchMap(({ createdItemResponse, payload }) => this.createAccountsReceivableForOrder(payload, createdItemResponse)),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: () => {
          this.errorService.showSuccessMessage('Pedido criado com sucesso!');
          this.navigateBack();
        },
        error: (err) => {
          this.errorService.handleHttpError(err);
        }
      });
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    } else {
      this.router.navigate(['/b2b/contracts']);
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.itemForm.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty));
  }

  getFieldError(fieldName: string): string {
    const field = this.itemForm.get(fieldName);
    if (field && field.invalid && (field.touched || field.dirty)) {
      if (field.errors?.['required']) return 'Campo obrigatório';
      if (field.errors?.['min']) return `Valor mínimo: ${field.errors['min'].min}`;
      if (field.errors?.['max']) return `Valor máximo: ${field.errors['max'].max}`;
    }
    return '';
  }

  isVehicleFieldInvalid(vehicleIndex: number, fieldName: string): boolean {
    const vehicle = this.vehicleSnapshots.at(vehicleIndex);
    if (!vehicle) return false;
    const field = vehicle.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty));
  }

  getVehicleFieldError(vehicleIndex: number, fieldName: string): string {
    const vehicle = this.vehicleSnapshots.at(vehicleIndex);
    if (!vehicle) return '';
    const field = vehicle.get(fieldName);
    if (field && field.invalid && (field.touched || field.dirty)) {
      if (field.errors?.['required']) return 'Campo obrigatório';
      if (field.errors?.['min']) return `Valor mínimo: ${field.errors['min'].min}`;
    }
    return '';
  }

  goToStep(step: number): void {
    if (step < 1 || step > this.totalSteps) return;
    if (step === 2 && this.itemForm.invalid) {
      this.itemForm.markAllAsTouched();
      this.vehicleSnapshots.controls.forEach(c => c.markAllAsTouched());
      this.errorService.showErrorMessage('Preencha os dados do pedido antes de ir para pagamentos.');
      return;
    }
    if (step === 2) {
      const fi = Number(this.itemForm.get('firstInstallmentAmount')?.value) || 0;
      if (fi <= 0) {
        this.itemForm.patchValue(
          { firstInstallmentAmount: Number(this.getOrderTotal().toFixed(2)) },
          { emitEvent: false }
        );
      }
      this.initOrderPaymentSplits();
      this.refreshInstallmentPreview();
    }
    this.currentStep = step;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getOrderTotal(): number {
    const unit = Number(this.itemForm.get('unitValue')?.value) || 0;
    const qty = Number(this.itemForm.get('vehicleQuantity')?.value) || 0;
    return Number((unit * qty).toFixed(2));
  }

  formatPrice(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
  }

  /** Valor de cada parcela mensal por veículo (Contas a Receber). */
  getValorParcelaPorVeiculo(): number {
    return Number(this.itemForm.get('unitValue')?.value) || 0;
  }

  /** Quantidade de parcelas em Contas a Receber por veículo (passo 1 — Número de Parcelas). */
  getParcelasPorVeiculo(): number {
    return Math.max(1, Math.min(24, Number(this.itemForm.get('installmentCount')?.value) || 1));
  }

  getDepositTotal(): number {
    return Number(this.itemForm.get('depositAmount')?.value) || 0;
  }

  getFirstInstallmentTotal(): number {
    return Number(this.itemForm.get('firstInstallmentAmount')?.value) || 0;
  }

  hasDepositBoleto(): boolean {
    return this.depositPayments?.some((p) => p.method === 'BOLETO') ?? false;
  }

  hasDepositCard(): boolean {
    return this.depositPayments?.some((p) => p.method === 'CREDIT_CARD') ?? false;
  }

  /** Ao alterar valores totais de caução ou 1ª parcela, redistribui os splits. */
  onDepositOrFirstTotalChange(): void {
    this.recalculateDepositSplit();
    this.recalculateFirstInstallmentSplit();
    this.refreshInstallmentPreview();
  }

  initOrderPaymentSplits(): void {
    const dep = this.getDepositTotal();
    const fi = this.getFirstInstallmentTotal();
    this.depositPayments = [
      { method: this.depositPayments[0]?.method || 'BOLETO', amount: dep },
      { method: this.depositPayments[1]?.method || 'PIX', amount: 0 }
    ];
    this.firstInstallmentPayments = [
      { method: this.firstInstallmentPayments[0]?.method || 'BOLETO', amount: fi },
      { method: this.firstInstallmentPayments[1]?.method || 'PIX', amount: 0 }
    ];
    this.depositPaymentMode = 'single';
    this.firstInstallmentPaymentMode = 'single';
    this.recalculateDepositSplit();
    this.recalculateFirstInstallmentSplit();
    this.refreshInstallmentPreview();
  }

  setDepositPaymentMode(mode: 'single' | 'split'): void {
    this.depositPaymentMode = mode;
    this.recalculateDepositSplit();
    this.refreshInstallmentPreview();
  }

  setFirstInstallmentPaymentMode(mode: 'single' | 'split'): void {
    this.firstInstallmentPaymentMode = mode;
    this.recalculateFirstInstallmentSplit();
    this.refreshInstallmentPreview();
  }

  private recalculateDepositSplit(): void {
    const total = this.getDepositTotal();
    if (total <= 0) {
      this.depositPayments[0].amount = 0;
      this.depositPayments[1].amount = 0;
      return;
    }
    if (this.depositPaymentMode === 'single') {
      this.depositPayments[0].amount = total;
      this.depositPayments[1].amount = 0;
    } else {
      let first = Number(this.depositPayments[0].amount) || 0;
      if (first <= 0 || first >= total) {
        first = Math.round((total / 2) * 100) / 100;
      }
      this.depositPayments[0].amount = first;
      this.depositPayments[1].amount = Number((total - first).toFixed(2));
    }
  }

  private recalculateFirstInstallmentSplit(): void {
    const total = this.getFirstInstallmentTotal();
    if (total <= 0) {
      this.firstInstallmentPayments[0].amount = 0;
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    if (this.firstInstallmentPaymentMode === 'single') {
      this.firstInstallmentPayments[0].amount = total;
      this.firstInstallmentPayments[1].amount = 0;
    } else {
      let first = Number(this.firstInstallmentPayments[0].amount) || 0;
      if (first <= 0 || first >= total) {
        first = Math.round((total / 2) * 100) / 100;
      }
      this.firstInstallmentPayments[0].amount = first;
      this.firstInstallmentPayments[1].amount = Number((total - first).toFixed(2));
    }
  }

  onDepositMethodChange(index: number, method: string): void {
    if (!this.depositPayments[index]) return;
    this.depositPayments[index].method = method;
    if (method === 'CREDIT_CARD') {
      this.depositPaymentMode = 'split';
      this.recalculateDepositSplit();
    }
    this.refreshInstallmentPreview();
  }

  onDepositAmountChange(index: number, value: number): void {
    const total = this.getDepositTotal();
    if (total <= 0) {
      this.depositPayments[0].amount = 0;
      this.depositPayments[1].amount = 0;
      return;
    }
    const v = Math.max(0, Number(value) || 0);
    if (this.depositPaymentMode === 'single') {
      this.depositPayments[0].amount = Math.min(v, total);
      this.depositPayments[1].amount = 0;
      return;
    }
    if (index === 0) {
      const first = Math.min(v, total);
      this.depositPayments[0].amount = first;
      this.depositPayments[1].amount = Number((total - first).toFixed(2));
    } else {
      const second = Math.min(v, total);
      this.depositPayments[1].amount = second;
      this.depositPayments[0].amount = Number((total - second).toFixed(2));
    }
    this.refreshInstallmentPreview();
  }

  onFirstInstallmentAmountChange(index: number, value: number): void {
    const total = this.getFirstInstallmentTotal();
    if (total <= 0) {
      this.firstInstallmentPayments[0].amount = 0;
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    const v = Math.max(0, Number(value) || 0);
    if (this.firstInstallmentPaymentMode === 'single') {
      this.firstInstallmentPayments[0].amount = Math.min(v, total);
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    if (index === 0) {
      const first = Math.min(v, total);
      this.firstInstallmentPayments[0].amount = first;
      this.firstInstallmentPayments[1].amount = Number((total - first).toFixed(2));
    } else {
      const second = Math.min(v, total);
      this.firstInstallmentPayments[1].amount = second;
      this.firstInstallmentPayments[0].amount = Number((total - second).toFixed(2));
    }
    this.refreshInstallmentPreview();
  }

  private setupInstallmentPreviewRefresh(): void {
    this.installmentPreviewSub = this.itemForm.valueChanges.subscribe(() => {
      this.refreshInstallmentPreview();
    });
  }

  refreshInstallmentPreview(): void {
    const installmentCount = this.getParcelasPorVeiculo();
    const unitValue = this.getUnitValueForReceivables();
    const firstDueDate = this.resolveFirstInstallmentDueDate({
      deliveryDate: this.itemForm.get('deliveryDate')?.value
    });
    const vehicles = this.getVehicleSnapshotsForPreview();

    const rows: Array<{
      plate: string;
      installmentNumber: number;
      dueDate: string;
      amount: number;
      isWeekend: boolean;
      overrideKey: string;
    }> = [];

    vehicles.forEach((veh, vIdx) => {
      const plate = this.plateFromVehicle(veh, vIdx);
      const plateToken = this.plateRefToken(plate);
      for (let i = 0; i < installmentCount; i++) {
        const installmentNumber = i + 1;
        const overrideKey = `${plateToken}-${installmentNumber}`;
        const suggested = this.addDaysToDateString(firstDueDate, i * 30);
        let dueDate = this.installmentDueDateOverrides[overrideKey] || suggested;
        if (this.adjustWeekendToPreviousBusinessDay && this.isWeekendIsoDate(dueDate)) {
          dueDate = this.toPreviousBusinessDayIso(dueDate);
          this.installmentDueDateOverrides[overrideKey] = dueDate;
        }
        rows.push({
          plate,
          installmentNumber,
          dueDate,
          amount: unitValue,
          isWeekend: this.isWeekendIsoDate(dueDate),
          overrideKey
        });
      }
    });
    this.installmentPreviewRows = rows;
  }

  onInstallmentDueDateChange(overrideKey: string, dueDate: string): void {
    if (!dueDate || !overrideKey) return;
    this.installmentDueDateOverrides[overrideKey] = dueDate;
    this.refreshInstallmentPreview();
  }

  onToggleAdjustWeekendToPreviousBusinessDay(checked: boolean): void {
    this.adjustWeekendToPreviousBusinessDay = checked;
    this.refreshInstallmentPreview();
  }

  isInstallmentWeekend(dueDate: string): boolean {
    return this.isWeekendIsoDate(dueDate);
  }

  /** Valor de cada parcela = valor unitário (uma série por veículo). */
  private getUnitValueForReceivables(): number {
    return Number(this.itemForm.get('unitValue')?.value) || 0;
  }

  private getVehicleSnapshotsForPreview(): any[] {
    const fromForm = this.vehicleSnapshots.controls.map((c) => c.value);
    if (fromForm.length > 0) return fromForm;
    const qty = Math.max(1, Number(this.itemForm.get('vehicleQuantity')?.value) || 1);
    return Array.from({ length: qty }, () => ({}));
  }

  /** Nome do contrato para títulos (não usar só o ID numérico). */
  private getContractLabelForReceivables(): string {
    const name = (this.contractName || this.contractB2b?.contractName || '').toString().trim();
    if (name) return name;
    const number = (this.contractNumber || this.contractB2b?.contractNumber || '').toString().trim();
    return number || (this.contractId ? `Contrato ${this.contractId}` : '');
  }

  private resolveInstallmentDueDate(overrideKey: string, installmentNumber: number, firstDueDate: string): string {
    const fromOverride = this.installmentDueDateOverrides[overrideKey];
    if (fromOverride) return fromOverride;
    const fromPreview = this.installmentPreviewRows.find((r) => r.overrideKey === overrideKey)?.dueDate;
    if (fromPreview) return fromPreview;
    return this.addDaysToDateString(firstDueDate, (installmentNumber - 1) * 30);
  }

  private isWeekendIsoDate(isoDate: string): boolean {
    const d = new Date(`${isoDate}T12:00:00`);
    if (isNaN(d.getTime())) return false;
    const day = d.getDay();
    return day === 0 || day === 6;
  }

  private toPreviousBusinessDayIso(isoDate: string): string {
    const d = new Date(`${isoDate}T12:00:00`);
    if (isNaN(d.getTime())) return isoDate;
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() - 1);
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private validateOrderPayments(): boolean {
    this.recalculateDepositSplit();
    this.recalculateFirstInstallmentSplit();

    const depositTotal = this.getDepositTotal();
    const depositSplitSum = this.depositPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    if (depositSplitSum > depositTotal + 0.01) {
      this.errorService.showErrorMessage('A soma dos valores da caução não pode ultrapassar o valor total da caução.');
      return false;
    }

    const firstTotal = this.getFirstInstallmentTotal();
    const firstSplitSum = this.firstInstallmentPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    if (firstSplitSum > firstTotal + 0.01) {
      this.errorService.showErrorMessage('A soma dos valores da 1ª parcela não pode ultrapassar o valor total da 1ª parcela.');
      return false;
    }

    for (let i = 0; i < this.extraCharges.length; i++) {
      const row = this.extraCharges[i];
      const desc = (row.description || '').trim();
      const amount = Number(row.amount) || 0;
      if (!desc && amount > 0) {
        this.errorService.showErrorMessage(`Selecione o tipo da cobrança adicional ${i + 1}.`);
        return false;
      }
      if (desc && amount <= 0) {
        this.errorService.showErrorMessage(`Informe um valor maior que zero na cobrança adicional ${i + 1}.`);
        return false;
      }
    }

    return true;
  }

  addExtraCharge(): void {
    this.extraCharges.push({
      description: '',
      amount: null,
      dueDate: this.getSuggestedDueDateForExtraCharge(this.extraCharges.length + 1)
    });
  }

  removeExtraCharge(index: number): void {
    if (index < 0 || index >= this.extraCharges.length) return;
    this.extraCharges.splice(index, 1);
  }

  private resetExtraCharges(): void {
    this.extraCharges = [
      {
        description: '',
        amount: null,
        dueDate: this.getSuggestedDueDateForExtraCharge(1)
      }
    ];
  }

  private getSuggestedDueDateForExtraCharge(position: number): string {
    const base = this.resolveFirstInstallmentDueDate({
      deliveryDate: this.itemForm.get('deliveryDate')?.value
    });
    return this.addDaysToDateString(base, (Math.max(1, position) - 1) * 30);
  }

  private validateDepositCardIfNeeded(): boolean {
    if (!this.hasDepositCard()) return true;
    if (!this.asaasCustomerInfo) {
      this.errorService.showErrorMessage('Não foi possível carregar os dados do cliente Asaas para pagamento com cartão.');
      return false;
    }
    const cardDeposits = this.depositPayments.filter((p) => p.amount && p.amount > 0 && p.method === 'CREDIT_CARD');
    for (let i = 0; i < cardDeposits.length; i++) {
      const useSecond = cardDeposits.length > 1 && i === 1;
      const num = useSecond ? this.creditCardNumber2 : this.creditCardNumber;
      const em = useSecond ? this.creditCardExpiryMonth2 : this.creditCardExpiryMonth;
      const ey = useSecond ? this.creditCardExpiryYear2 : this.creditCardExpiryYear;
      const cvv = useSecond ? this.creditCardCcv2 : this.creditCardCcv;
      if (!num?.replace(/\D/g, '') || !em || !ey || !cvv) {
        this.errorService.showErrorMessage('Preencha todos os dados do cartão da caução.');
        return false;
      }
    }
    return true;
  }

  onCreditCardNumberInput(event: Event, cardIndex: 1 | 2 = 1): void {
    const input = event.target as HTMLInputElement;
    let digits = (input.value || '').replace(/\D/g, '').slice(0, 16);
    const groups = digits.match(/.{1,4}/g) || [];
    const formatted = groups.join(' ');
    if (cardIndex === 1) this.creditCardNumber = formatted;
    else this.creditCardNumber2 = formatted;
  }

  private loadAsaasCustomerByCpfCnpj(cpfCnpj: string): void {
    const digits = (cpfCnpj || '').replace(/\D/g, '');
    if (!digits || (digits.length !== 11 && digits.length !== 14)) {
      this.asaasCustomerId = null;
      this.asaasCustomerError = null;
      this.asaasCustomerInfo = null;
      return;
    }
    this.asaasCustomerLoading = true;
    this.asaasCustomerError = null;
    this.paymentService.listAsaasCustomers({ cpfCnpj: digits }).subscribe({
      next: (res) => {
        this.asaasCustomerLoading = false;
        const first = res?.data?.[0];
        this.asaasCustomerId = first?.id ?? null;
        this.asaasCustomerInfo = first || null;
        if (!this.asaasCustomerId) {
          this.asaasCustomerError =
            'Cliente de pagamento não encontrado no Asaas para este CNPJ. Ao salvar o pedido, o sistema tentará criar o cadastro automaticamente.';
        }
      },
      error: (err) => {
        this.asaasCustomerLoading = false;
        this.asaasCustomerId = null;
        this.asaasCustomerInfo = null;
        this.asaasCustomerError = err?.message || 'Erro ao consultar cliente no Asaas.';
      }
    });
  }

  /** Há PIX, boleto, cartão ou parcelas de veículo que dependem do Asaas. */
  private needsAsaasOrderPayments(itemData: any): boolean {
    const hasAsaasMethod = (rows: { method: string; amount: number }[]) =>
      rows.some((p) => (Number(p.amount) || 0) > 0 && ['PIX', 'BOLETO', 'CREDIT_CARD'].includes(p.method));
    if (hasAsaasMethod(this.depositPayments) || hasAsaasMethod(this.firstInstallmentPayments)) {
      return true;
    }
    const unit = Number(itemData?.unitValue) || 0;
    const vehicles = itemData?.vehicleSnapshots || [];
    return unit > 0 && vehicles.length > 0;
  }

  /**
   * Garante `asaasCustomerId` antes de gerar cobranças (lista por CNPJ ou cria cliente — alinhado ao contrato padrão).
   */
  private ensureAsaasCustomerId$(): Observable<string | null> {
    if (this.asaasCustomerId) {
      return of(this.asaasCustomerId);
    }
    const c = this.contractB2b;
    if (!c) {
      return of(null);
    }
    const raw = String(c.cnpj ?? (c as any).Cnpj ?? '').replace(/\D/g, '');
    if (!raw || (raw.length !== 11 && raw.length !== 14)) {
      return of(null);
    }
    return this.paymentService.listAsaasCustomers({ cpfCnpj: raw }).pipe(
      switchMap((res) => {
        const first = res?.data?.[0];
        if (first?.id) {
          this.asaasCustomerId = first.id;
          this.asaasCustomerInfo = first;
          this.asaasCustomerError = null;
          return of(first.id);
        }
        const payload: AsaasCustomer = {
          name: c.companyName || c.customerName || 'Cliente B2B',
          cpfCnpj: raw,
          email: (c as any).email || undefined,
          mobilePhone: (c as any).phoneNumber || (c as any).primaryPhone || (c as any).phone || undefined,
          phone: (c as any).phoneNumber || (c as any).phone || undefined,
          postalCode: ((c as any).zipCode || '').toString().replace(/\D/g, '') || undefined,
          addressNumber: (c as any).addressNumber || 'S/N'
        };
        return this.paymentService.createAsaasCustomer(payload).pipe(
          map((created) => {
            const id = created?.id ?? null;
            if (id) {
              this.asaasCustomerId = id;
              this.asaasCustomerInfo = { ...payload, id };
              this.asaasCustomerError = null;
            }
            return id;
          }),
          catchError(() => of(null))
        );
      }),
      catchError(() => of(null))
    );
  }

  private getTodayDateString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private createAccountsReceivableForOrder(itemData: any, createdItemResponse?: any): Observable<void> {
    // Pedidos novos entram no fechamento mensal — não gera parcelas antecipadas.
    if (itemData?.usesMonthlyClosing === true || itemData?.UsesMonthlyClosing === true) {
      return of(undefined);
    }

    const batch = this.buildAccountsReceivableBatchForOrder(itemData, createdItemResponse);
    if (batch.length === 0) {
      return of(undefined);
    }
    return this.accountsReceivableService.createAccountsReceivableBatch(batch).pipe(
      map(() => undefined),
      catchError((err) => {
        this.errorService.handleHttpError(err);
        return of(undefined);
      })
    );
  }

  private buildAccountsReceivableBatchForOrder(itemData: any, createdItemResponse?: any): AccountsReceivableCreate[] {
    if (!this.contractId) return [];

    const issueDate = this.getTodayDateString();
    const firstDueDate = this.resolveFirstInstallmentDueDate(itemData);
    const installmentCount = Math.max(1, Math.min(24, Number(itemData?.installmentCount) || 1));
    const unitValue = Number(itemData?.unitValue) || 0;
    if (unitValue <= 0) return [];

    const vehicles: any[] = Array.isArray(itemData?.vehicleSnapshots) && itemData.vehicleSnapshots.length > 0
      ? itemData.vehicleSnapshots
      : this.getVehicleSnapshotsForPreview();
    if (vehicles.length === 0) return [];

    const contractLabel = this.getContractLabelForReceivables();
    // Para evitar colisões quando o backend repetir `itemNumber` (ex: "001"), priorizamos o `id`
    const orderNumber =
      createdItemResponse?.id ||
      createdItemResponse?.Id ||
      createdItemResponse?.itemNumber ||
      createdItemResponse?.ItemNumber ||
      `NOVO-${Date.now()}`;

    const contractCustomerId = Number(this.contractB2b?.customerId || this.contractB2b?.CustomerId || 0) || undefined;
    const saleOrderNumber = `B2B-CONTRATO-${this.contractId}`;

    const batch: AccountsReceivableCreate[] = [];
    vehicles.forEach((veh, vIdx) => {
      const plate = this.plateFromVehicle(veh, vIdx);
      const plateToken = this.plateRefToken(plate);
      for (let i = 0; i < installmentCount; i++) {
        const installmentNumber = i + 1;
        const overrideKey = `${plateToken}-${installmentNumber}`;
        const dueDate = this.resolveInstallmentDueDate(overrideKey, installmentNumber, firstDueDate);
        batch.push({
          contractNumber: contractLabel || undefined,
          documentNumber: `PED-${plateToken}-${orderNumber}-${installmentNumber}`.slice(0, 60),
          documentType: 'PED',
          issueDate,
          dueDate,
          originalAmount: unitValue,
          amountToReceive: unitValue,
          status: 'Pendente',
          category: 'Venda',
          customerId: contractCustomerId,
          costCenterId: 1,
          description: `${contractLabel} | Pedido ${orderNumber} | Placa ${plate} | Parcela ${installmentNumber}/${installmentCount}`,
          saleOrderNumber,
          installmentNumber,
          totalInstallments: installmentCount
        });
      }
    });

    const validExtraCharges = this.extraCharges
      .map((charge) => ({
        description: (charge.description || '').trim(),
        amount: Number(charge.amount) || 0,
        dueDate: charge.dueDate || firstDueDate
      }))
      .filter((charge) => charge.description && charge.amount > 0);

    validExtraCharges.forEach((charge, index) => {
      batch.push({
        contractNumber: contractLabel || undefined,
        documentNumber: `PED-EXTRA-${orderNumber}-${index + 1}`.slice(0, 60),
        documentType: 'PED',
        issueDate,
        dueDate: charge.dueDate,
        originalAmount: charge.amount,
        amountToReceive: charge.amount,
        status: 'Pendente',
        category: 'Venda',
        customerId: contractCustomerId,
        costCenterId: 1,
        description: `${contractLabel} | Pedido ${orderNumber} | ${charge.description}`,
        saleOrderNumber
      });
    });
    return batch;
  }

  private resolveFirstInstallmentDueDate(itemData: any): string {
    const deliveryDate = itemData?.deliveryDate ? this.isoToDateInput(itemData.deliveryDate) : '';
    if (deliveryDate) return deliveryDate;
    return this.getTodayDateString();
  }

  private addDaysToDateString(dateStr: string, daysToAdd: number): string {
    const base = new Date(dateStr);
    if (isNaN(base.getTime())) return dateStr;
    const d = new Date(base.getTime());
    d.setDate(d.getDate() + daysToAdd);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  /** Divide valor total em partes iguais (centavos) para uma cobrança por placa no Asaas. */
  private splitAmountAcrossVehicles(total: number, count: number): number[] {
    if (count <= 0) return [];
    const cents = Math.round(Number(total) * 100);
    if (cents <= 0) return Array(count).fill(0);
    const base = Math.floor(cents / count);
    const rem = cents - base * count;
    const out: number[] = [];
    for (let i = 0; i < count; i++) {
      out.push((base + (i < rem ? 1 : 0)) / 100);
    }
    return out;
  }

  private plateFromVehicle(veh: any, index: number): string {
    const raw = (veh?.plate ?? '').toString().trim();
    if (raw) return raw.toUpperCase();
    return `MOTO-${index + 1}`;
  }

  /** Trecho curto para externalReference (evita estourar limite do Asaas). */
  private plateRefToken(plate: string): string {
    const t = plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    return (t || 'SEMPLACA').slice(0, 10);
  }

  /**
   * Fluxo alinhado ao contrato: 1ª parcela (PIX/Boleto/Cartão), caução (PIX/Cartão/Boleto),
   * depois um batch de boletos por veículo (N parcelas × valor unitário), tudo rotulado por placa no Asaas.
   */
  private triggerAsaasOrderPayments(itemData: any): Observable<void> {
    if (!this.asaasCustomerId || !this.contractId) return of(undefined);

    const customerId = this.asaasCustomerId;
    const today = this.getTodayDateString();
    const dueDateBase = today;
    const orderRef = `B2B-${this.contractId}-${Date.now()}`;
    const vehicles: any[] = itemData.vehicleSnapshots || [];
    const plates: string[] =
      vehicles.length > 0
        ? vehicles.map((v, i) => this.plateFromVehicle(v, i))
        : ['PEDIDO'];
    const nVeh = plates.length;
    const contractLabel = (this.contractNumber || String(this.contractId)).toString().trim();
    const prefixDesc = `B2B Contrato ${contractLabel} | Pedido #${this.contractId}`;

    const observables: Observable<unknown>[] = [];
    const onError = (msg: string) => (err: any) => {
      this.errorService.showErrorMessage(msg + (err?.error?.message || err?.message || 'Erro desconhecido'));
      return of(null);
    };

    // 1) 1ª parcela — PIX ou Boleto (valor dividido por placa quando há várias motos)
    this.firstInstallmentPayments
      .filter((p) => p.amount && p.amount > 0 && (p.method === 'PIX' || p.method === 'BOLETO'))
      .forEach((p, rowIndex) => {
        const parts = this.splitAmountAcrossVehicles(Number(p.amount), nVeh);
        parts.forEach((amt, vIdx) => {
          if (amt <= 0) return;
          const pl = plates[vIdx];
          const charge: AsaasCharge = {
            customerId,
            billingType: p.method as 'PIX' | 'BOLETO',
            value: amt,
            dueDate: dueDateBase,
            description: `${prefixDesc} | 1ª parcela ${p.method} | Placa ${pl}`,
            externalReference: `${orderRef}-P1-${p.method}-R${rowIndex + 1}-V${vIdx + 1}-${this.plateRefToken(pl)}`
          };
          observables.push(
            this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar cobrança da 1ª parcela: ')))
          );
        });
      });

    // 1b) 1ª parcela — Cartão (usa os mesmos dados de cartão da caução: método 1 / método 2)
    const cardFirst = this.firstInstallmentPayments.filter((p) => p.amount && p.amount > 0 && p.method === 'CREDIT_CARD');
    if (cardFirst.length > 0 && this.asaasCustomerInfo) {
      const customerInfo = this.asaasCustomerInfo as AsaasCustomer;
      cardFirst.forEach((p, rowIndex) => {
        const parts = this.splitAmountAcrossVehicles(Number(p.amount), nVeh);
        const useSecond = cardFirst.length > 1 && rowIndex === 1;
        const holderName = useSecond
          ? this.creditCardHolderName2 || customerInfo.name
          : this.creditCardHolderName || customerInfo.name;
        const cardNumber = useSecond ? this.creditCardNumber2 : this.creditCardNumber;
        const expiryMonth = useSecond ? this.creditCardExpiryMonth2 : this.creditCardExpiryMonth;
        const expiryYear = useSecond ? this.creditCardExpiryYear2 : this.creditCardExpiryYear;
        const cvv = useSecond ? this.creditCardCcv2 : this.creditCardCcv;
        const installmentsForThisCard = 1;
        parts.forEach((partVal, vIdx) => {
          if (partVal <= 0) return;
          const pl = plates[vIdx];
          const charge: AsaasCharge = {
            customerId,
            billingType: 'CREDIT_CARD',
            value: partVal,
            dueDate: dueDateBase,
            description: `${prefixDesc} | 1ª parcela Cartão | Placa ${pl}`,
            externalReference: `${orderRef}-P1-CARD-R${rowIndex + 1}-V${vIdx + 1}-${this.plateRefToken(pl)}`,
            installmentCount: installmentsForThisCard,
            creditCard: {
              holderName,
              number: (cardNumber || '').replace(/\s/g, ''),
              expiryMonth: (expiryMonth || '').toString(),
              expiryYear: (expiryYear || '').toString(),
              ccv: (cvv || '').toString()
            },
            creditCardHolderInfo: {
              name: customerInfo.name,
              email: customerInfo.email || '',
              cpfCnpj: customerInfo.cpfCnpj,
              postalCode: customerInfo.postalCode || '',
              addressNumber: customerInfo.addressNumber || '',
              addressComplement: customerInfo.addressComplement || '',
              phone: customerInfo.phone || customerInfo.mobilePhone || '',
              mobilePhone: customerInfo.mobilePhone || customerInfo.phone || ''
            }
          };
          observables.push(
            this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar 1ª parcela no cartão: ')))
          );
        });
      });
    }

    // 2) Caução — PIX (por placa)
    this.depositPayments
      .filter((p) => p.amount && p.amount > 0 && p.method === 'PIX')
      .forEach((p, rowIndex) => {
        const parts = this.splitAmountAcrossVehicles(Number(p.amount), nVeh);
        parts.forEach((amt, vIdx) => {
          if (amt <= 0) return;
          const pl = plates[vIdx];
          const charge: AsaasCharge = {
            customerId,
            billingType: 'PIX',
            value: amt,
            dueDate: dueDateBase,
            description: `${prefixDesc} | Caução PIX | Placa ${pl}`,
            externalReference: `${orderRef}-CAUC-PIX-R${rowIndex + 1}-V${vIdx + 1}-${this.plateRefToken(pl)}`
          };
          observables.push(
            this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar PIX da caução: ')))
          );
        });
      });

    // 3) Caução — Cartão (por placa)
    const cardDeposits = this.depositPayments.filter((p) => p.amount && p.amount > 0 && p.method === 'CREDIT_CARD');
    if (cardDeposits.length > 0 && this.asaasCustomerInfo) {
      const customerInfo = this.asaasCustomerInfo as AsaasCustomer;
      cardDeposits.forEach((p, index) => {
        const value = Number(p.amount);
        if (!value || value <= 0) return;
        const parts = this.splitAmountAcrossVehicles(value, nVeh);
        const useSecond = cardDeposits.length > 1 && index === 1;
        const holderName = useSecond
          ? this.creditCardHolderName2 || customerInfo.name
          : this.creditCardHolderName || customerInfo.name;
        const cardNumber = useSecond ? this.creditCardNumber2 : this.creditCardNumber;
        const expiryMonth = useSecond ? this.creditCardExpiryMonth2 : this.creditCardExpiryMonth;
        const expiryYear = useSecond ? this.creditCardExpiryYear2 : this.creditCardExpiryYear;
        const cvv = useSecond ? this.creditCardCcv2 : this.creditCardCcv;
        const installmentsForThisCard = Math.max(
          1,
          Math.min(10, useSecond ? this.depositCardInstallments2 || 1 : this.depositCardInstallments || 1)
        );
        parts.forEach((partVal, vIdx) => {
          if (partVal <= 0) return;
          const pl = plates[vIdx];
          const charge: AsaasCharge = {
            customerId,
            billingType: 'CREDIT_CARD',
            value: partVal,
            dueDate: dueDateBase,
            description: `${prefixDesc} | Caução Cartão (${installmentsForThisCard}x) | Placa ${pl}`,
            externalReference: `${orderRef}-CAUC-CARD-${index + 1}-V${vIdx + 1}-${this.plateRefToken(pl)}`,
            installmentCount: installmentsForThisCard,
            creditCard: {
              holderName,
              number: (cardNumber || '').replace(/\s/g, ''),
              expiryMonth: (expiryMonth || '').toString(),
              expiryYear: (expiryYear || '').toString(),
              ccv: (cvv || '').toString()
            },
            creditCardHolderInfo: {
              name: customerInfo.name,
              email: customerInfo.email || '',
              cpfCnpj: customerInfo.cpfCnpj,
              postalCode: customerInfo.postalCode || '',
              addressNumber: customerInfo.addressNumber || '',
              addressComplement: customerInfo.addressComplement || '',
              phone: customerInfo.phone || customerInfo.mobilePhone || '',
              mobilePhone: customerInfo.mobilePhone || customerInfo.phone || ''
            }
          };
          observables.push(
            this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar cobrança no cartão da caução: ')))
          );
        });
      });
    }

    // 4) Caução — Boleto: total em boleto dividido por placa; cada placa com seu lote (à vista ou N parcelas da caução)
    const boletoDepositTotal = this.depositPayments
      .filter((p) => p.amount && p.amount > 0 && p.method === 'BOLETO')
      .reduce((sum, p) => sum + Number(p.amount), 0);
    const depInst = Math.max(1, Math.min(10, this.depositInstallments || 1));
    if (boletoDepositTotal > 0) {
      const perVehicle = this.splitAmountAcrossVehicles(boletoDepositTotal, nVeh);
      perVehicle.forEach((val, vIdx) => {
        if (val <= 0) return;
        const pl = plates[vIdx];
        if (depInst === 1) {
          const charge: AsaasCharge = {
            customerId,
            billingType: 'BOLETO',
            value: val,
            dueDate: dueDateBase,
            description: `${prefixDesc} | Caução boleto à vista | Placa ${pl}`,
            externalReference: `${orderRef}-CAUC-BO-1x-V${vIdx + 1}-${this.plateRefToken(pl)}`
          };
          observables.push(
            this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar boleto da caução: ')))
          );
        } else {
          const totalCents = Math.round(val * 100);
          const baseCents = Math.floor(totalCents / depInst);
          const depositBatch: AsaasChargeBatch[] = [];
          for (let i = 0; i < depInst; i++) {
            const isLast = i === depInst - 1;
            const cents = isLast ? totalCents - baseCents * i : baseCents;
            depositBatch.push({
              customerId,
              value: cents / 100,
              dueDate: dueDateBase,
              description: `${prefixDesc} | Caução boleto ${i + 1}/${depInst} | Placa ${pl}`,
              externalReference: `${orderRef}-CAUC-BO-${i + 1}of${depInst}-V${vIdx + 1}-${this.plateRefToken(pl)}`
            });
          }
          observables.push(
            this.paymentService.createAsaasChargeBatch(depositBatch).pipe(catchError(onError('Erro ao gerar boletos da caução: ')))
          );
        }
      });
    }

    // 5) Mensalidades do pedido — um batch por veículo: `instCount` boletos por placa
    const unit = Number(itemData.unitValue) || 0;
    const instCount = Math.max(1, Math.min(24, Number(itemData.installmentCount) || 12));
    if (unit > 0 && instCount > 0 && vehicles.length > 0) {
      vehicles.forEach((veh: any, vIdx: number) => {
        const plateRaw = this.plateFromVehicle(veh, vIdx);
        const plateCompact = this.plateRefToken(plateRaw);
        const batch: AsaasChargeBatch[] = [];
        for (let k = 0; k < instCount; k++) {
          const dueDate = this.addDaysToDateString(today, k * 30);
          batch.push({
            customerId,
            value: unit,
            dueDate,
            description: `${prefixDesc} | Mensalidade ${k + 1}/${instCount} | Placa ${plateRaw}`,
            externalReference: `${orderRef}-MENS-${k + 1}of${instCount}-${plateCompact}`
          });
        }
        observables.push(
          this.paymentService
            .createAsaasChargeBatch(batch)
            .pipe(catchError(onError(`Erro ao gerar mensalidades da placa ${plateRaw}: `)))
        );
      });
    }

    if (observables.length === 0) return of(undefined);
    return forkJoin(observables).pipe(map(() => undefined), catchError(() => of(undefined)));
  }
}
